/**
 * @fileoverview Tag-side membership editing: the expanded product list panel and
 * the add-products picker (rendered as a row action in TagRow).
 * @module features/tags/components/TagProductsPanel
 */
import { useMemo, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Package, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  useTagProductsQuery,
  useSetTagProductsMutation,
  useProductCandidatesQuery,
} from '../api/tags'
import type { ProductSummary } from '../schemas/tagSchema'

interface TagProductsPanelProps {
  tagUuid: string
}

export function TagProductsPanel({ tagUuid }: TagProductsPanelProps): ReactElement {
  const { t } = useTranslation('features-tags')
  const { data: members, isLoading, isError } = useTagProductsQuery(tagUuid)
  const setProducts = useSetTagProductsMutation()

  const handleRemove = (id: string): void => {
    setProducts.mutate(
      {
        tagUuid,
        productExternalIds: (members ?? [])
          .map(member => member.id)
          .filter(memberId => memberId !== id),
      },
      { onError: () => toast.error(t('products.saveError', 'Failed to update tagged products.')) }
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-6 w-64" />
      </div>
    )
  }

  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {t('products.loadError', 'Failed to load tagged products.')}
      </p>
    )
  }

  if ((members ?? []).length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('products.empty', 'No products tagged yet.')}
      </p>
    )
  }

  return (
    <ul>
      {(members ?? []).map((member, index) => (
        <li
          key={member.id}
          className="flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/40"
        >
          <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
            {index + 1}.
          </span>
          <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-input/50 bg-muted/30">
            {member.imageUrl ? (
              <img src={member.imageUrl} alt="" className="size-full object-cover" />
            ) : (
              <Package className="size-4 text-muted-foreground" />
            )}
          </span>
          <Link
            to={`/dashboard/management/products/${member.id}`}
            className="min-w-0 flex-1 truncate text-sm text-foreground hover:underline"
          >
            {member.name}
          </Link>
          <button
            type="button"
            aria-label={t('products.removeAria', {
              name: member.name,
              defaultValue: 'Remove {{name}}',
            })}
            onClick={() => handleRemove(member.id)}
            disabled={setProducts.isPending}
            className="shrink-0 text-muted-foreground transition hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </li>
      ))}
    </ul>
  )
}

interface AddTagProductsControlProps {
  tagUuid: string
}

/**
 * Icon-button popover for adding products to a tag (lives in the row's action
 * bar). Members are fetched lazily when the picker opens; adding is blocked
 * until they arrive so the full-replace PUT can't drop existing memberships.
 */
export function AddTagProductsControl({ tagUuid }: AddTagProductsControlProps): ReactElement {
  const { t } = useTranslation('features-tags')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data: members } = useTagProductsQuery(open ? tagUuid : null)
  const setProducts = useSetTagProductsMutation()
  const { data: candidates, isLoading: candidatesLoading } = useProductCandidatesQuery(search, {
    enabled: open,
  })

  const memberIds = useMemo(() => new Set((members ?? []).map(member => member.id)), [members])
  const availableCandidates = useMemo(
    () => (candidates ?? []).filter(candidate => !memberIds.has(candidate.id)),
    [candidates, memberIds]
  )

  const handleOpenChange = (next: boolean): void => {
    setOpen(next)
    if (!next) setSearch('')
  }

  const handleAdd = (product: ProductSummary): void => {
    if (!members) return
    setProducts.mutate(
      { tagUuid, productExternalIds: [...members.map(member => member.id), product.id] },
      { onError: () => toast.error(t('products.saveError', 'Failed to update tagged products.')) }
    )
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('products.addProducts', 'Add products')}
          disabled={setProducts.isPending}
        >
          <Plus className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('products.searchPlaceholder', 'Search products...')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="scrollbar-subtle">
            {candidatesLoading || !members ? (
              <div className="p-2">
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <>
                <CommandEmpty>{t('products.noResults', 'No products found.')}</CommandEmpty>
                {(candidates ?? []).length > 0 && availableCandidates.length === 0 && (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                    {t('products.allAdded', 'All products are already in this tag.')}
                  </p>
                )}
                <CommandGroup>
                  {availableCandidates.map(candidate => (
                    <CommandItem
                      key={candidate.id}
                      value={candidate.id}
                      onSelect={() => handleAdd(candidate)}
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded border border-input/50 bg-muted/30">
                        {candidate.imageUrl ? (
                          <img
                            src={candidate.imageUrl}
                            alt=""
                            className="size-full object-cover"
                          />
                        ) : (
                          <Package className="size-3 text-muted-foreground" />
                        )}
                      </span>
                      <span className="truncate">{candidate.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
